import { useEffect, useRef, useState } from 'react';

export interface PollingResource<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  /** Force an immediate refetch outside the polling cadence. */
  refetch: () => void;
}

interface PollingOptions<T> {
  fetcher: () => Promise<T>;
  intervalMs: number;
  /** Skip the tick when document is hidden (default true). */
  pauseOnBlur?: boolean;
  /** Dependency list to restart polling when it changes. */
  deps?: ReadonlyArray<unknown>;
  /**
   * WQ-286 Item 4: when true, skip the fetch entirely. Used by ThreatsTab
   * to short-circuit when the operator has deselected all operator_state
   * chips (sending no param would mean "all states" — opposite of intent).
   * Data + error reset to null; isLoading becomes false.
   */
  skip?: boolean;
}

/**
 * Generic polling hook used by the four security data-fetching hooks.
 * - Initial fetch on mount.
 * - Periodic refetch at `intervalMs`.
 * - `pauseOnBlur`: when document is hidden, skip the scheduled tick. On
 *   `visibilitychange` back to visible, fetch immediately.
 * - Tracks the latest in-flight request and only commits its result if no
 *   newer request has started — prevents race-condition staleness.
 */
export function usePollingResource<T>({
  fetcher,
  intervalMs,
  pauseOnBlur = true,
  deps = [],
  skip = false,
}: PollingOptions<T>): PollingResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Trigger to force a refetch outside the polling cadence.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (skip) {
      // Reset state to empty + non-loading on skip; bump request id so any
      // in-flight prior fetcher's setData is dropped on completion.
      requestIdRef.current++;
      setData(null);
      setError(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const doFetch = async () => {
      const myId = ++requestIdRef.current;
      try {
        const next = await fetcherRef.current();
        if (cancelled || myId !== requestIdRef.current) return;
        setData(next);
        setError(null);
      } catch (e) {
        if (cancelled || myId !== requestIdRef.current) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled && myId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    doFetch();
    const handle = setInterval(() => {
      if (pauseOnBlur && typeof document !== 'undefined' && document.hidden) return;
      doFetch();
    }, intervalMs);

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) doFetch();
    };
    if (pauseOnBlur && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      cancelled = true;
      clearInterval(handle);
      if (pauseOnBlur && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, pauseOnBlur, tick, skip, ...deps]);

  return {
    data,
    isLoading,
    error,
    refetch: () => setTick((t) => t + 1),
  };
}
